import {
  Button,
  Checkbox,
  H2,
  H3,
  Icon,
  InputGroup,
  Intent,
  MenuItem,
  Spinner,
  SpinnerSize,
} from '@blueprintjs/core';
import { Tooltip2 } from '@blueprintjs/popover2';
import { Select2 } from '@blueprintjs/select';
import * as React from 'react';
import DataTable from 'react-data-table-component';
import { useHistory } from 'react-router-dom';

import { useAppDispatch } from 'src/ui/app/hooks';
import { Loading } from 'src/ui/components/Loading';
import {
  CLOUD_REPORT_CLEANUP_BUTTON,
  CLOUD_REPORT_DAYS_SEARCH_BUTTON,
  CLOUD_REPORT_FILTERS_CONTAINER,
  CLOUD_REPORT_INACTIVE_FILTER,
  CLOUD_REPORT_MOVE_TO_ORG_BUTTON,
  CLOUD_REPORT_QUEUE_MEDIUM_COUNT,
  CLOUD_REPORT_QUEUE_MICRO_COUNT,
  CLOUD_REPORT_QUEUE_SMALL_VBX_COUNT,
  CLOUD_REPORT_SDU_DOWNLOAD_BUTTON,
  CLOUD_REPORT_SEARCH_BUTTON,
  CLOUD_REPORT_TABLE,
  CLOUD_REPORT_TRANSFER_BUTTON,
  CLOUD_REPORT_VALIDATION_WARNING,
} from 'src/ui/constants/testIds';
import { CleanupOrgConnectionDialog } from 'src/ui/containers/internal/CleanupOrgConnectionDialog';
import { MoveConnectionToOrgDialog } from 'src/ui/containers/internal/MoveConnectionToOrgDialog';
import { TransferCloudToUserDialog } from 'src/ui/containers/internal/TransferCloudToUserDialog';
import { queueMessage } from 'src/ui/features/notifications';
import { graphQLClient } from 'src/ui/graph/graphQLClient';
import { getSdk } from 'src/ui/graph/sdk';
import {
  CloudFilters,
  CloudSduStatus,
  StardogCloud,
  useGetCloudReportQuery,
  useGetCloudSduLifetimeQuery,
  useProfileQuery,
} from 'src/ui/graph/types';
import { useQueryParams } from 'src/ui/hooks/query';
import * as copy from 'src/ui/templates/copy';
import {
  buildSduUsageCsv,
  formatSduUnits,
} from 'src/ui/utils/buildSduUsageCsv';
import { exportToFile } from 'src/ui/utils/exportToFile';

const flavorOptions = ['micro', 'small', 'small-vbx', 'medium'];
const sduCopy =
  copy.components.internalDashboard.home.actionGrid.cloudReport.sdu;

type CloudReportExpandedRowContextValue = {
  canManageOrgs: boolean;
  onCleanup: (cloudId: string) => void;
  onMove: (cloudId: string) => void;
  onTransfer: (cloudId: string) => void;
  rowIndexByCloudId: Map<string | null | undefined, number>;
};

const CloudReportExpandedRowContext = React.createContext<CloudReportExpandedRowContextValue | null>(
  null
);

const LifetimeSduValue: React.VFC<{ cloudId: string }> = ({ cloudId }) => {
  const { data, isError, isLoading } = useGetCloudSduLifetimeQuery(
    graphQLClient,
    { cloud_ids: [cloudId] },
    {
      retry: false,
    }
  );
  const lifetime = data?.getCloudSduLifetime?.find(
    (record) => record.cloudId === cloudId
  );

  if (isLoading) {
    return <Spinner aria-label={sduCopy.loading} size={SpinnerSize.SMALL} />;
  }
  if (
    lifetime?.status === CloudSduStatus.Ok &&
    lifetime.totalUnitsConsumed != null
  ) {
    const formatted = formatSduUnits(lifetime.totalUnitsConsumed);
    return (
      <span aria-label={sduCopy.lifetimeValue(formatted)}>{formatted}</span>
    );
  }
  const unavailableCopy =
    lifetime?.status === CloudSduStatus.Error || isError
      ? sduCopy.error
      : sduCopy.unavailable;
  return (
    <Tooltip2 content={unavailableCopy}>
      <span aria-label={unavailableCopy}>
        {copy.components.sduDashboard.noData}
      </span>
    </Tooltip2>
  );
};

const CloudReportExpandedRow: React.VFC<{ data: StardogCloud }> = ({
  data,
}) => {
  const ctx = React.useContext(CloudReportExpandedRowContext);
  if (!ctx) {
    return null;
  }
  const cloudId = data.id;
  const rowIndex = ctx.rowIndexByCloudId.get(cloudId);
  const hasRowActions = !!(
    data.move_available ||
    data.cleanup_available ||
    data.transfer_available
  );
  return (
    <div className="cloud-report__expanded-row">
      <div className="cloud-report__expanded-grid">
        <div className="cloud-report__detail-item">
          <dt>Endpoint</dt>
          <dd className="cloud-report__endpoint-code">{data.endpoint}</dd>
        </div>

        <div className="cloud-report__detail-item">
          <dt>Created Date</dt>
          <dd>
            {data.created
              ? new Date(data.created).toLocaleString()
              : 'Not available'}
          </dd>
        </div>

        <div className="cloud-report__detail-item">
          <dt>Owner Last Login</dt>
          <dd>
            {data.owner?.last_login ? (
              new Date(data.owner.last_login).toLocaleString()
            ) : (
              <em className="cloud-report__never-logged">Never logged in</em>
            )}
          </dd>
        </div>

        <div className="cloud-report__detail-item">
          <dt>Instance Details</dt>
          <dd className="cloud-report__instance-details">
            <div>
              <strong>Flavor:</strong> {data.flavor?.name || 'Unknown'}
            </div>
            {cloudId ? (
              <div>
                <strong>{sduCopy.columnHeader}:</strong>{' '}
                <LifetimeSduValue cloudId={cloudId} />
              </div>
            ) : null}
            {data.region ? (
              <div>
                <strong>Region:</strong> {data.region}
              </div>
            ) : null}
            {data.payment_ref ? (
              <div>
                <strong>Payment Ref:</strong> {data.payment_ref}
              </div>
            ) : null}
          </dd>
        </div>

        {data.flavor?.name === 'micro' ? (
          <div className="cloud-report__detail-item">
            <dt>
              <Tooltip2 content="Automatic cleanup settings for free tier micro instances">
                Lifecycle Management
              </Tooltip2>
            </dt>
            <dd className="cloud-report__instance-details">
              <div>
                <Tooltip2 content="Number of days of user inactivity before marking cloud as inactive and sending cleanup notification">
                  <>
                    <strong>Days to Inactive:</strong>{' '}
                    {data.days_to_inactive
                      ? `${data.days_to_inactive} days`
                      : 'Not set'}
                  </>
                </Tooltip2>
              </div>
              <div>
                <Tooltip2 content="Number of days after notification before permanent deletion">
                  <>
                    <strong>Days to Cleanup:</strong>{' '}
                    {data.days_to_cleanup
                      ? `${data.days_to_cleanup} days`
                      : 'Not set'}
                  </>
                </Tooltip2>
              </div>
              <div>
                <Tooltip2 content="When the user was notified about their inactive cloud instance">
                  <>
                    <strong>Notification Date:</strong>{' '}
                    {data.notification_date ? (
                      new Date(data.notification_date).toLocaleString()
                    ) : (
                      <em className="cloud-report__never-logged">
                        Never notified
                      </em>
                    )}
                  </>
                </Tooltip2>
              </div>
            </dd>
          </div>
        ) : null}

        {ctx.canManageOrgs && cloudId && hasRowActions ? (
          <div className="cloud-report__detail-item cloud-report__expanded-actions">
            {data.move_available ? (
              <Button
                data-testid={CLOUD_REPORT_MOVE_TO_ORG_BUTTON(cloudId, rowIndex)}
                intent="primary"
                onClick={() => ctx.onMove(cloudId)}
                text={copy.components.moveConnectionToOrgDialog.menu}
              />
            ) : null}
            {data.transfer_available ? (
              <Button
                data-testid={CLOUD_REPORT_TRANSFER_BUTTON(cloudId, rowIndex)}
                onClick={() => ctx.onTransfer(cloudId)}
                text={copy.components.transferCloudToUserDialog.menu}
              />
            ) : null}
            {data.cleanup_available ? (
              <Button
                data-testid={CLOUD_REPORT_CLEANUP_BUTTON(cloudId, rowIndex)}
                onClick={() => ctx.onCleanup(cloudId)}
                text={copy.components.cleanupOrgConnectionDialog.menu}
              />
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export const BASE_PATH = '/internal/cloud/report';
const buildPath = (filters: CloudFilters) => {
  const params = new URLSearchParams();

  if (filters.ownerInactiveDays) {
    params.set(
      'ownerInactive',
      encodeURIComponent(filters.ownerInactiveDays.toString())
    );
  }
  if (filters.status) {
    params.set('status', encodeURIComponent(filters.status));
  }
  if (filters.flavor) {
    params.set('flavor', encodeURIComponent(filters.flavor));
  }
  if (filters.search) {
    params.set('search', encodeURIComponent(filters.search));
  }

  const queryString = params.toString();
  return `${BASE_PATH}${queryString ? `?${queryString}` : ''}`;
};

export const CloudReport: React.VFC = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const urlParams = useQueryParams();
  const [currentPage, setCurrentPage] = React.useState(1);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const filters = React.useMemo((): CloudFilters => {
    const days = urlParams.get('ownerInactive')
      ? parseInt(decodeURIComponent(urlParams.get('ownerInactive') || '0'), 10)
      : undefined;
    return {
      ownerInactiveDays: days && days > 0 ? days : undefined,
      status: urlParams.get('status')
        ? decodeURIComponent(urlParams.get('status') || '') || undefined
        : undefined,
      flavor: urlParams.get('flavor')
        ? decodeURIComponent(urlParams.get('flavor') || '') || undefined
        : undefined,
      search: urlParams.get('search')
        ? decodeURIComponent(urlParams.get('search') || '') || undefined
        : undefined,
    };
  }, [urlParams]);

  const { data, isLoading } = useGetCloudReportQuery(graphQLClient, {
    filters,
  });
  const reportClouds = React.useMemo(
    () =>
      (data?.getCloudReport?.clouds ?? []).filter(
        (cloud): cloud is StardogCloud => cloud !== null
      ),
    [data]
  );
  const rowIndexByCloudId = React.useMemo(
    () => new Map(reportClouds.map((cloud, index) => [cloud.id, index])),
    [reportClouds]
  );

  const { data: profileData } = useProfileQuery(graphQLClient);
  const canManageOrgs = !!profileData?.profile?.can_manage_orgs;
  const canSeeValidation =
    canManageOrgs || !!profileData?.profile?.is_superuser;

  const [daysInput, setDaysInput] = React.useState(
    filters.ownerInactiveDays?.toString() || ''
  );
  const [searchInput, setSearchInput] = React.useState(filters.search || '');
  const [moveCloudId, setMoveCloudId] = React.useState<string | null>(null);
  const [cleanupCloudId, setCleanupCloudId] = React.useState<string | null>(
    null
  );
  const [transferCloudId, setTransferCloudId] = React.useState<string | null>(
    null
  );
  const [downloadingCloudId, setDownloadingCloudId] = React.useState<
    string | null
  >(null);

  React.useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  const updateFilters = React.useCallback(
    (newFilters: Partial<CloudFilters>) => {
      const updatedFilters = { ...filters, ...newFilters };
      const newPath = buildPath(updatedFilters);
      history.replace(newPath);
    },
    [history, filters]
  );

  const handleDaysSubmit = React.useCallback(() => {
    const days = daysInput ? parseInt(daysInput, 10) : undefined;
    const validDays = days && days > 0 ? days : undefined;
    updateFilters({ ownerInactiveDays: validDays });
  }, [daysInput, updateFilters]);

  const handleSearchSubmit = React.useCallback(() => {
    updateFilters({ search: searchInput || undefined });
  }, [searchInput, updateFilters]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number, page: number) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(page);
  };

  const handleDownloadSdu = React.useCallback(
    async (cloud: StardogCloud) => {
      if (!cloud.id) {
        return;
      }

      const label = cloud.name || cloud.endpoint || '';
      setDownloadingCloudId(cloud.id);
      dispatch(
        queueMessage({
          intent: Intent.NONE,
          message: copy.components.stardogUnitUsage.downloadAttempting(label),
        })
      );

      try {
        const result = await getSdk(graphQLClient).getCloudSduUsage({
          cloud_id: cloud.id,
        });
        const records = result.getCloudSduUsage;
        if (!records.length) {
          dispatch(
            queueMessage({
              intent: Intent.WARNING,
              message: copy.components.stardogUnitUsage.downloadNoData(label),
            })
          );
          return;
        }

        const rangeStart = records[0].date;
        const rangeEnd = records[records.length - 1].date;

        exportToFile({
          contents: buildSduUsageCsv(
            records.map((record) => ({
              date: record.date,
              units: record.totalUnitsConsumed,
            })),
            { name: cloud.name || '', url: cloud.endpoint || '' }
          ),
          filename: `stardog-unit-usage-${
            cloud.name || cloud.id
          }-${rangeStart}-${rangeEnd}.csv`,
          mimeType: 'text/csv',
        });
        dispatch(
          queueMessage({
            intent: Intent.SUCCESS,
            message: copy.components.stardogUnitUsage.downloadSuccess(label),
          })
        );
      } catch {
        dispatch(
          queueMessage({
            intent: Intent.DANGER,
            message: sduCopy.downloadFailed(label),
          })
        );
      } finally {
        setDownloadingCloudId((current) =>
          current === cloud.id ? null : current
        );
      }
    },
    [dispatch]
  );

  if (isLoading || !data?.getCloudReport) {
    return <Loading />;
  }

  const { stats, queueCounts } = data.getCloudReport;

  if (!stats || !queueCounts) {
    return <Loading />;
  }

  const isFiltered = !!(
    filters.ownerInactiveDays ||
    filters.status ||
    filters.flavor ||
    filters.search
  );

  const microCount = queueCounts.micro_count;
  const smallVbxCount = queueCounts.small_vbx_count;
  const mediumCount = queueCounts.medium_count;

  // Helper function to get status badge class
  const getStatusBadgeClass = (status: string | null | undefined): string => {
    const baseClass = 'cloud-report__status-badge';
    switch (status?.toLowerCase()) {
      case 'active':
        return `${baseClass} cloud-report__status-badge--active`;
      case 'inactive':
        return `${baseClass} cloud-report__status-badge--inactive`;
      default:
        return `${baseClass} cloud-report__status-badge--default`;
    }
  };

  const expandedRowContext = {
    canManageOrgs,
    onCleanup: setCleanupCloudId,
    onMove: setMoveCloudId,
    onTransfer: setTransferCloudId,
    rowIndexByCloudId,
  };

  const validationCopy =
    copy.components.internalDashboard.home.actionGrid.cloudReport.validation;

  const columns = [
    {
      name: 'Name',
      id: 1,
      selector: (row: StardogCloud) => row?.name || '',
      sortable: true,
    },
    {
      name: (
        <Tooltip2 content="Active = endpoint in normal use, Inactive = marked for reclamation">
          Status
        </Tooltip2>
      ),
      id: 2,
      selector: /* istanbul ignore next */ (row: StardogCloud) =>
        row?.status || '',
      sortable: true,
      cell: (row: StardogCloud) => (
        <span className={getStatusBadgeClass(row.status)}>
          {row.status || 'Unknown'}
        </span>
      ),
    },
    {
      name: (
        <Tooltip2 content="The user who owns this endpoint">Owner</Tooltip2>
      ),
      id: 3,
      selector: (row: StardogCloud) => row?.owner?.username || '',
      sortable: true,
    },
    {
      name: 'Flavor',
      id: 4,
      selector: (row: StardogCloud) => row?.flavor?.name || '',
      sortable: true,
    },
    {
      name: 'Endpoint',
      id: 5,
      selector: (row: StardogCloud) => row?.endpoint || '',
      sortable: true,
      wrap: true,
    },
    ...(canSeeValidation
      ? [
          {
            name: (
              <Tooltip2 content={validationCopy.columnTooltip}>
                {validationCopy.columnHeader}
              </Tooltip2>
            ),
            id: 7,
            selector: /* istanbul ignore next */ (row: StardogCloud) =>
              row?.validation?.length || 0,
            sortable: true,
            cell: (row: StardogCloud) =>
              row.validation?.length ? (
                <Tooltip2
                  content={
                    <div className="cloud-report__validation-messages">
                      {row.validation.map((message) => (
                        <div key={`${row.id}-${message}`}>{message}</div>
                      ))}
                    </div>
                  }
                >
                  <Icon
                    aria-label={validationCopy.warningAriaLabel}
                    data-testid={CLOUD_REPORT_VALIDATION_WARNING}
                    icon="warning-sign"
                    intent="warning"
                  />
                </Tooltip2>
              ) : null,
          },
        ]
      : []),
    {
      name: sduCopy.actionsHeader,
      id: 8,
      button: true,
      grow: 0,
      minWidth: '220px',
      wrap: false,
      cell: (row: StardogCloud) => {
        return (
          <Button
            className="cloud-report__sdu-download"
            data-testid={CLOUD_REPORT_SDU_DOWNLOAD_BUTTON(
              row.id || '',
              rowIndexByCloudId.get(row.id)
            )}
            icon="download"
            loading={downloadingCloudId === row.id}
            minimal
            onClick={(event) => {
              event.stopPropagation();
              handleDownloadSdu(row);
            }}
            text={sduCopy.download}
          />
        );
      },
    },
  ];

  const endpointsCountText = isFiltered ? (
    <div className="cloud-report__endpoints-stats">
      <div className="cloud-report__endpoints-stat">
        <div className="cloud-report__endpoints-stat-count">
          {stats.resultCount}
        </div>
        <div className="cloud-report__endpoints-stat-label">
          {filters.ownerInactiveDays
            ? `owners inactive ${filters.ownerInactiveDays}+ days`
            : 'matching filters'}
        </div>
      </div>
      <div className="cloud-report__endpoints-stat">
        <div className="cloud-report__endpoints-stat-count">
          {stats.totalAllocated}
        </div>
        <div className="cloud-report__endpoints-stat-label">
          total allocated
        </div>
      </div>
      <div className="cloud-report__endpoints-stat">
        <div className="cloud-report__endpoints-stat-count">
          {stats.totalInactive}
        </div>
        <div className="cloud-report__endpoints-stat-label">total inactive</div>
      </div>
    </div>
  ) : (
    <div className="cloud-report__endpoints-stats">
      <div className="cloud-report__endpoints-stat">
        <div className="cloud-report__endpoints-stat-count">
          {stats.totalAllocated}
        </div>
        <div className="cloud-report__endpoints-stat-label">
          total allocated
        </div>
      </div>
      <div className="cloud-report__endpoints-stat">
        <div className="cloud-report__endpoints-stat-count">
          {stats.totalInactive}
        </div>
        <div className="cloud-report__endpoints-stat-label">inactive</div>
      </div>
    </div>
  );

  return (
    <CloudReportExpandedRowContext.Provider value={expandedRowContext}>
      <div className="sd-gutter">
        <div>
          <H2 className="cloud-report__title">Cloud Endpoints Report</H2>

          {/* Queue Status and Summary */}
          <div className="cloud-report__queue-container">
            <H3 className="cloud-report__queue-title">
              Available Cloud Endpoints in Queue
            </H3>
            <div className="cloud-report__queue-items">
              <div className="cloud-report__queue-stat">
                <div
                  className="cloud-report__queue-stat-count"
                  data-testid={CLOUD_REPORT_QUEUE_MICRO_COUNT}
                >
                  {microCount}
                </div>
                <div className="cloud-report__queue-stat-label">micros</div>
              </div>
              <div className="cloud-report__queue-stat">
                <div
                  className="cloud-report__queue-stat-count"
                  data-testid={CLOUD_REPORT_QUEUE_SMALL_VBX_COUNT}
                >
                  {smallVbxCount}
                </div>
                <div className="cloud-report__queue-stat-label">small-vbx</div>
              </div>
              <div className="cloud-report__queue-stat">
                <div
                  className="cloud-report__queue-stat-count"
                  data-testid={CLOUD_REPORT_QUEUE_MEDIUM_COUNT}
                >
                  {mediumCount}
                </div>
                <div className="cloud-report__queue-stat-label">mediums</div>
              </div>
            </div>
          </div>

          {/* Cloud Endpoints Section */}
          <div className="cloud-report__endpoints-container">
            <H3 className="cloud-report__endpoints-title">Cloud Endpoints</H3>

            <div className="cloud-report__endpoints-summary">
              {endpointsCountText}
            </div>

            <div
              className="cloud-report__filters-container"
              data-testid={CLOUD_REPORT_FILTERS_CONTAINER}
            >
              <div className="cloud-report__filters">
                {/* Status Filter */}
                <div className="cloud-report__filter-section">
                  <span className="cloud-report__filter-title">Status</span>
                  <div className="cloud-report__filter-controls">
                    <Checkbox
                      checked={filters.status === 'inactive'}
                      onChange={(evt) => {
                        const isChecked = evt.currentTarget.checked;
                        updateFilters({
                          status: isChecked ? 'inactive' : undefined,
                        });
                      }}
                      label="Show inactive only"
                      data-testid={CLOUD_REPORT_INACTIVE_FILTER}
                    />
                  </div>
                </div>

                <div className="cloud-report__filter-section">
                  <span className="cloud-report__filter-title">
                    Owner Activity
                  </span>
                  <div className="cloud-report__filter-controls">
                    <div className="cloud-report__filter-days">
                      <span className="cloud-report__filter-days-label">
                        Owner has not logged in for (days)
                      </span>
                      <div className="cloud-report__filter-days-controls">
                        <InputGroup
                          type="number"
                          placeholder="Days"
                          value={daysInput}
                          onChange={(evt) => setDaysInput(evt.target.value)}
                          min="1"
                          onKeyDown={
                            /* istanbul ignore next */ (evt) => {
                              if (evt.key === 'Enter') {
                                handleDaysSubmit();
                              }
                            }
                          }
                        />
                        <Button
                          icon="search"
                          minimal
                          onClick={handleDaysSubmit}
                          data-testid={CLOUD_REPORT_DAYS_SEARCH_BUTTON}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="cloud-report__filter-row">
                  <div className="cloud-report__filter-flavor">
                    <span className="cloud-report__filter-title">Flavor</span>
                    <Select2
                      items={[...flavorOptions, 'All Flavors']}
                      itemRenderer={(flavor, { handleClick, modifiers }) => (
                        <MenuItem
                          key={flavor}
                          active={modifiers.active}
                          onClick={handleClick}
                          text={flavor}
                        />
                      )}
                      onItemSelect={(flavor) => {
                        updateFilters({
                          flavor: flavor === 'All Flavors' ? undefined : flavor,
                        });
                      }}
                      filterable={false}
                      popoverProps={{ minimal: true }}
                    >
                      <Button
                        text={filters.flavor || 'All Flavors'}
                        rightIcon="caret-down"
                      />
                    </Select2>
                  </div>

                  <div className="cloud-report__filter-search">
                    <span className="cloud-report__filter-title">Search</span>
                    <InputGroup
                      type="search"
                      name="cloud-filter"
                      placeholder="Search by name, owner, or endpoint"
                      value={searchInput}
                      onChange={(evt) => setSearchInput(evt.target.value)}
                      onKeyDown={(evt) => {
                        if (evt.key === 'Enter') {
                          handleSearchSubmit();
                        }
                      }}
                      rightElement={
                        searchInput !== filters.search ? (
                          <Button
                            icon="search"
                            minimal
                            onClick={handleSearchSubmit}
                            data-testid={CLOUD_REPORT_SEARCH_BUTTON}
                          />
                        ) : undefined
                      }
                    />
                  </div>
                </div>
              </div>
            </div>

            <div data-testid={CLOUD_REPORT_TABLE}>
              <DataTable
                noHeader
                columns={columns}
                data={reportClouds}
                pagination
                paginationDefaultPage={currentPage}
                paginationRowsPerPageOptions={[25, 50, 100, 150]}
                paginationPerPage={rowsPerPage}
                onChangePage={handlePageChange}
                onChangeRowsPerPage={handleRowsPerPageChange}
                dense
                responsive
                highlightOnHover
                expandableRows
                expandableRowsComponent={CloudReportExpandedRow}
                expandOnRowClicked
              />
            </div>
          </div>
        </div>
        {moveCloudId ? (
          <MoveConnectionToOrgDialog
            cloudId={moveCloudId}
            isOpen={!!moveCloudId}
            onClose={() => setMoveCloudId(null)}
          />
        ) : null}
        {cleanupCloudId ? (
          <CleanupOrgConnectionDialog
            cloudId={cleanupCloudId}
            isOpen={!!cleanupCloudId}
            onClose={() => setCleanupCloudId(null)}
          />
        ) : null}
        {transferCloudId ? (
          <TransferCloudToUserDialog
            cloudId={transferCloudId}
            isOpen={!!transferCloudId}
            onClose={() => setTransferCloudId(null)}
          />
        ) : null}
      </div>
    </CloudReportExpandedRowContext.Provider>
  );
};
